// MatchLineup domain types -- projected from iteration 021 changes.md

export interface MatchLineupEntryPublic {
  id: string;
  match_id: string;
  team_id: string;
  player_id: string;
  player_name: string;
  jersey_number: number;
  is_captain: boolean;
  is_vice_captain: boolean;
  covers_positions: number[];
  // 153: BokSmart front-row backup designation
  backup_hooker: boolean;
  backup_prop: boolean;
}

export type LineupAction = "edit" | "delete";

export interface MatchLineupResponse {
  match_id: string;
  team_id: string;
  team_name: string;
  sport_name: string | null;
  lineup: MatchLineupEntryPublic[];
  team_template_available: boolean;
  _actions: LineupAction[];
}

export interface MatchLineupsResponse {
  match_id: string;
  lineups: MatchLineupResponse[];
}

// Position constants

export interface PositionDef {
  number: number;
  name: string;
  abbreviation: string;
  group: string;
  row: number;
  col: number;
}

export interface SportPositions {
  starters: number;
  reserves: number;
  positions: PositionDef[];
}

/** Rugby Union: 15 starters + 8 reserves = 23 */
export const RUGBY_UNION_POSITIONS: SportPositions = {
  starters: 15,
  reserves: 8,
  positions: [
    // Front Row (row 0)
    { number: 1, name: "Loosehead Prop", abbreviation: "PROP", group: "forwards", row: 0, col: 0 },
    { number: 2, name: "Hooker", abbreviation: "HOOKER", group: "forwards", row: 0, col: 1 },
    { number: 3, name: "Tighthead Prop", abbreviation: "PROP", group: "forwards", row: 0, col: 2 },
    // Second Row (row 1)
    { number: 4, name: "Lock", abbreviation: "SECOND ROW", group: "forwards", row: 1, col: 0 },
    { number: 5, name: "Lock", abbreviation: "SECOND ROW", group: "forwards", row: 1, col: 1 },
    // Back Row (row 2)
    { number: 6, name: "Blindside Flanker", abbreviation: "FLANKER", group: "forwards", row: 2, col: 0 },
    { number: 7, name: "Openside Flanker", abbreviation: "FLANKER", group: "forwards", row: 2, col: 2 },
    { number: 8, name: "Number Eight", abbreviation: "NUMBER 8", group: "forwards", row: 2, col: 1 },
    // Halfbacks (row 3)
    { number: 9, name: "Scrum Half", abbreviation: "SCRUM HALF", group: "backs", row: 3, col: 0 },
    { number: 10, name: "Fly Half", abbreviation: "FLY HALF", group: "backs", row: 3, col: 1 },
    // Centres (row 4)
    { number: 12, name: "Inside Centre", abbreviation: "CENTRE", group: "backs", row: 4, col: 0 },
    { number: 13, name: "Outside Centre", abbreviation: "CENTRE", group: "backs", row: 4, col: 1 },
    // Back Three (row 5)
    { number: 11, name: "Left Wing", abbreviation: "WING", group: "backs", row: 5, col: 0 },
    { number: 15, name: "Fullback", abbreviation: "FULL BACK", group: "backs", row: 5, col: 1 },
    { number: 14, name: "Right Wing", abbreviation: "WING", group: "backs", row: 5, col: 2 },
    // Reserves (row 6) -- bench
    { number: 16, name: "Reserve Hooker", abbreviation: "RES", group: "reserves", row: 6, col: 0 },
    { number: 17, name: "Reserve Prop", abbreviation: "RES", group: "reserves", row: 6, col: 1 },
    { number: 18, name: "Reserve Prop", abbreviation: "RES", group: "reserves", row: 6, col: 2 },
    { number: 19, name: "Reserve Lock", abbreviation: "RES", group: "reserves", row: 6, col: 3 },
    { number: 20, name: "Reserve Flanker", abbreviation: "RES", group: "reserves", row: 6, col: 4 },
    { number: 21, name: "Reserve Scrum Half", abbreviation: "RES", group: "reserves", row: 6, col: 5 },
    { number: 22, name: "Reserve Fly Half", abbreviation: "RES", group: "reserves", row: 6, col: 6 },
    { number: 23, name: "Reserve Back", abbreviation: "RES", group: "reserves", row: 6, col: 7 },
  ],
};

/**
 * Map of sport name → sport positions.
 * Extensible: add netball, hockey, etc. as needed.
 */
export const POSITIONS_BY_SPORT: Record<string, SportPositions> = {
  "Rugby": RUGBY_UNION_POSITIONS,
};

/** Get position definitions for a sport name, or undefined if not mapped */
export function getPositionsForSport(sportName: string): SportPositions | undefined {
  return POSITIONS_BY_SPORT[sportName];
}

/** Lookup a single position by jersey number within a sport's positions */
export function getPositionByNumber(positions: SportPositions, jerseyNumber: number): PositionDef | undefined {
  return positions.positions.find((p) => p.number === jerseyNumber);
}

/** Check if a jersey number is a reserve (bench) position */
export function isReservePosition(sportPositions: SportPositions, jerseyNumber: number): boolean {
  return jerseyNumber > sportPositions.starters;
}

// --- Team Position types (default formation, independent of matches) ---

export interface TeamPositionEntryPublic {
  id: string;
  team_id: string;
  player_id: string;
  player_name: string;
  jersey_number: number;
  is_captain: boolean;
  is_vice_captain: boolean;
  covers_positions: number[];
}

export type TeamPositionAction = "edit" | "delete";

export interface TeamPositionResponse {
  team_id: string;
  team_name: string;
  sport_name: string | null;
  positions: TeamPositionEntryPublic[];
  _actions: TeamPositionAction[];
}

export interface SetPlayerPositionInput {
  jersey_number: number;
  is_captain: boolean;
  is_vice_captain: boolean;
  secondary_positions: number[];
}
